import { useForm, Controller, useFieldArray } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import Select from "react-select";
import { Button, Row, Col, FormGroup, Label } from "reactstrap";
import { useNotificationContext } from "contexts";
import { FormEntry, FormSelect, ImageUpload, FormTextArea } from "components";
import apiService from "services/api";
import { removeEmptyStringFields } from "utils/objectCleaning";
import { encodeImageFileAsURL } from "utils/fileToBase64";
import { useRef } from "react";
import { initialState, languages, examinations } from "./constants";

const NewStudentPage = () => {
	const history = useHistory();
	const fileInputRef = useRef();

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		control,
	} = useForm({
		defaultValues: initialState,
		revalidateMode: "onChange",
	});

	const { data: parents, isLoading: isParentLoading } = useQuery("parent", () => apiService.parent.get());

	const { data: centres, isLoading: isLoadingCentres } = useQuery(["centre"], () =>
		apiService.student.centres().then((centres) =>
			centres.map((centre) => ({
				label: centre.shortName,
				value: centre.id,
				data: centre,
			}))
		)
	);

	const { createNotification } = useNotificationContext();

	const submitNewStudent = async (data) => {
		const formData = removeEmptyStringFields(data);
		let imgPath = undefined;

		if (fileInputRef.current.files.length) {
			const file = fileInputRef.current.files[0];
			imgPath = await encodeImageFileAsURL(file);
		}
		console.log(formData.age);

		apiService.student
			.create({ ...formData, ...(imgPath ? { imgPath } : {}) })
			.then(() => {
				createNotification({
					type: "success",
					message: "New student creation is successful",
				});
				history.push("/student");
			})
			.catch((error) => createNotification({ type: "danger", message: error.message }));
	};

	return (
		<form onSubmit={handleSubmit(submitNewStudent)} className="content">
			<div className="d-flex justify-content-center">
				<div className="w-100">
					<h4 className="text-center mb-4">Add New Student</h4>
					<div className="mb-5">
						<Link to="/student">
							<button type="button" className="btn btn-sm btn-info">&lt; Back to Students</button>
						</Link>
					</div>
					<Row className="mb-5">
						<Col md={6}>
							<div className="text-light ml-2 mb-2">
								<small>Required</small>
							</div>
							<div className="input-group mb-3">
								<div className="input-group-prepend">
									<span className="input-group-text">Name</span>
								</div>
								<input
									type="text"
									className="form-control"
									name="firstName"
									{...register("firstName", {
										required: true,
									})}
									placeholder="First Name"
									required
								/>
								<input
									type="text"
									className="form-control"
									name="lastName"
									{...register("lastName", {
										required: true,
									})}
									placeholder="Last Name"
									required
								/>
								{errors.firstName && (
									<div className=" text-center">
										<small className="text-danger">{errors.firstName?.message}</small>
									</div>
								)}
								{errors.lastName && (
									<div className=" text-center">
										<small className="text-danger">{errors.lastName?.message}</small>
									</div>
								)}
							</div>
							<FormEntry
								formLabel="Student No"
								register={register}
								name="studentNo"
								error={errors.studentNo}
								placeholder="Student No"
								type="text"
								required={true}
							/>
							<div className="input-group mb-3">
								<div className="input-group-prepend">
									<label className="input-group-text pr-2" htmlFor="gender">
										Parent
									</label>
								</div>
								<Controller
									rules={{
										required: {
											value: true,
											message: "Parent is required",
										},
									}}
									control={control}
									name="parentId"
									render={({ field: { ref } }) => (
										<Select
											inputRef={ref}
											className="react-select info"
											classNamePrefix="react-select"
											onChange={({ value }) => setValue("parentId", value)}
											isDisabled={isParentLoading}
											options={parents?.map(
												(parent) =>
													({
														value: parent.id,
														label: `${parent.firstName} ${parent.lastName}`,
													} || [])
											)}
										/>
									)}
								/>
							</div>
							{errors.parentId && (
								<div className=" text-center">
									<small className="text-danger">{errors.parentId?.message}</small>
								</div>
							)}
							<div className="input-group mb-3">
								<div className="input-group-prepend">
									<label className="input-group-text pr-2" htmlFor="centre">
										Centre
									</label>
								</div>
								<Controller
									rules={{
										required: {
											value: true,
											message: "Centre is required",
										},
									}}
									control={control}
									name="centreId"
									render={({ field: { ref, value, onChange } }) => (
										<Select
											inputRef={ref}
											className="react-select info"
											classNamePrefix="react-select"
											value={centres?.find((c) => value === c.value)}
											onChange={(val) => onChange(val.value)}
											options={centres || []}
											placeholder="Select centres...."
										/>
									)}
								/>
							</div>
							{errors.centreId && (
								<div className=" text-center">
									<small className="text-danger">{errors.centreId?.message}</small>
								</div>
							)}
							<div className="input-group mb-3">
								<FormEntry
									formLabel="DOB"
									register={register}
									name="dob"
									error={errors.dob}
									placeholder="Student Age"
									type="date"
									required={true}
								/>
							</div>
							<div className="input-group mb-3">
								<FormTextArea
									register={register}
									formLabel="Additional Info"
									placeholder="Additional Info"
									name="additionalInfo"
									error={errors.additionalInfo}
								/>
							</div>
                            <div className="input-group mb-3">
								<FormTextArea
									register={register}
									formLabel="Background Information"
									placeholder="Additional Info"
									name="backgroundInformation"
									error={errors.backgroundInformation}
								/>
							</div>
						</Col>
						<Col md={6} className="text-center">
							<FormGroup>
								<Label for="imgPath" className="text-white">
									Student Picture
								</Label>
							</FormGroup>
							<ImageUpload hasButton={false} fileInputRef={fileInputRef} />
							{errors.imgPath && (
								<div className=" text-center">
									<small className="text-danger">{errors.imgPath?.message}</small>
								</div>
							)}
						</Col>
					</Row>
					<Row>
						<Col>
							<div className="text-light ml-2 mb-2">
								<small>Optional</small>
							</div>
							<FormEntry
								register={register}
								placeholder="Student parent car number plate 1"
								formLabel={"Car Number plate 1"}
								type="text"
								name="carNumber1"
								error={errors.carNumber1}
							/>
							<FormEntry
								register={register}
								placeholder="Student parent car number plate 2"
								formLabel={"Car Number plate 2"}
								type="text"
								name="carNumber2"
								error={errors.carNumber2}
							/>
							<FormEntry
								formLabel="Birthday"
								placeholder="Birthday"
								type="date"
								register={register}
								name="birthday"
								error={errors.birthday}
							/>
							<FormEntry
								formLabel="Student's Email"
								placeholder="Student's Email"
								type="email"
								register={register}
								name="email"
								error={errors.email}
							/>
							<FormSelect
								formLabel="Primary Language"
								register={register}
								options={languages}
								error={errors.primaryLanguage}
								name="primaryLanguage"
								required
							/>
							<FormEntry
								formLabel="Current School"
								placeholder="Current School"
								type="text"
								register={register}
								name="currentSchool"
								error={errors.currentSchool}
							/>
							<FormEntry
								formLabel="Current Grade"
								placeholder="Current Grade"
								type="text"
								register={register}
								name="currentGrade"
								error={errors.currentGrade}
							/>
							<FormSelect
								formLabel="Public exams to be taken"
								register={register}
								options={examinations}
								error={errors.publicExams}
								name="publicExams"
							/>
							<FormEntry
								formLabel="Personality Type"
								placeholder="Personality"
								type="text"
								register={register}
								name="personalityType"
								error={errors.personalityType}
							/>
							<FormEntry
								formLabel="Parent's Special Request"
								placeholder="Request"
								type="text"
								register={register}
								name="specialRequest"
								error={errors.specialRequest}
							/>
							<FormEntry
								formLabel="Allergies / Medical Conditions"
								placeholder="Allergies"
								type="text"
								register={register}
								name="allergiesCondition"
								error={errors.allergiesCondition}
							/>
						</Col>
						<Col>
							<div className="text-light ml-2 mb-2">
								<small>Optional</small>
							</div>
							<FormEntry
								formLabel="Favourite Subjects"
								placeholder="Subjects"
								type="text"
								register={register}
								name="favouriteSubjects"
								error={errors.favouriteSubjects}
							/>
							<FormEntry
								formLabel="Least Favourite Subjects"
								placeholder="Least Favourite Subject"
								type="text"
								register={register}
								name="leastFavouriteSubjects"
								error={errors.leastFavouriteSubjects}
							/>
							<FormEntry
								formLabel="Favourite Colour"
								placeholder="Colour"
								type="text"
								register={register}
								name="favouriteColour"
								error={errors.favouriteColour}
							/>
							<FormEntry
								formLabel="Favourite Food"
								placeholder="Food"
								type="text"
								register={register}
								name="favouriteFood"
								error={errors.favouriteFood}
							/>
							<FormEntry
								formLabel="Favourite Animal"
								placeholder="Animal"
								type="text"
								register={register}
								name="favouriteAnimal"
								error={errors.favouriteAnimal}
							/>
							<FormEntry
								formLabel="Favourite Hobby"
								placeholder="Hobby"
								type="text"
								register={register}
								name="favouriteHobby"
								error={errors.favouriteHobby}
							/>
							<FormEntry
								formLabel="Favourite Video Games"
								placeholder="Video Games"
								type="text"
								register={register}
								name="favouriteVideoGames"
								error={errors.favouriteVideoGames}
							/>
							<FormEntry
								formLabel="Favourite Movies"
								placeholder="Movies"
								type="text"
								register={register}
								name="favouriteMovies"
								error={errors.favouriteMovies}
							/>
							<FormEntry
								formLabel="Favourite Sport"
								placeholder="Sport"
								type="text"
								register={register}
								name="favouriteSport"
								error={errors.favouriteSport}
							/>
						</Col>
					</Row>
					<div className="d-flex justify-content-end mt-3">
						<Button variant="sm" color="secondary" type="reset" className="mr-2">
							Reset
						</Button>
						<Button variant="sm" color="primary" type="submit">
							Submit
						</Button>
					</div>
				</div>
			</div>
		</form>
	);
};

export default NewStudentPage;
